import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Mail, AlertCircle, Eye, EyeOff } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(email, password);
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα σύνδεσης. Δοκιμάστε ξανά.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <img 
            src="/logo-white.png" 
            alt="Ραπτόπουλος Workplace" 
            className="h-24 mx-auto mb-4"
          />
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-xl p-8 border-2 border-slate-200 shadow-xl">
          <h2 className="font-chivo text-xl font-bold text-slate-900 uppercase mb-6 text-center">
            Σύνδεση
          </h2>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">
                Email
              </Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-rugged w-full"
                placeholder="email@example.com"
                required
              />
            </div>

            <div>
              <Label className="text-sm font-bold uppercase text-slate-500 mb-2 block">
                Κωδικός
              </Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-rugged w-full pr-12"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              className="btn-rugged w-full mt-6"
              disabled={loading}
            >
              {loading ? 'Σύνδεση...' : 'Είσοδος'}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Ξεχάσατε τον κωδικό σας; Επικοινωνήστε με τον διαχειριστή.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
